import App from "./App.svelte";
import {siteConfig, langConfig, PrivacyConsentEnum} from "./config";
import Cookie from "js-cookie";
import base64 from "Base64";
import cookieWhitelist from "./cookieWhitelist.json";
import packageJson from "../package.json";

export const CONSENT_COOKIE = "PRIVACY_CONSENT";
export const PREV_SEEN_COOKIE = "banner-cookieinfo";
export const COOKIE_EXPIRES = 365;

export default class PrivacyConsent {
  constructor(options) {
    this.hostHasSSL = window.location.protocol === 'https:' ? true : false;
    this.options = {
      ...options,
    };
    this.__callbacks = {};
    this.__cookieStore = [];
    this.__invokeStore = [];
    this.__cookieWhitelist = this.__getValidCookieWhitelist();

    this.registerCallback("onModalOpen", this.__onModalOpen.bind(this));
    this.registerCallback("onModalClose", this.__onModalClose.bind(this));
    this.registerCallback("onModalClose", this.__track.bind(this));
    this.registerCallback("statusChanged", this.__onStatusChanged.bind(this));

    if (!Cookie.get(PREV_SEEN_COOKIE) && this.__getTld() === 'de' && this.__getDomain() !== 'localhost') {
      this.__removeConsent();
      Cookie.set(PREV_SEEN_COOKIE, packageJson.version, {
        path: '/',
        domain: this.__getDomain(),
        expires: COOKIE_EXPIRES,
        secure:  this.hostHasSSL,
        sameSite: this.hostHasSSL ? 'None' : undefined
      });
    }
  }

  __getValidCookieWhitelist() {
    return cookieWhitelist.map(({name, consentCategory, domain, path}) => ({
      name, consentCategory, props: {
        domain, path,
      },
    }))
  }

  registerCallback(event, callback, scope) {
    this.__log(`registered callback ${event}`, scope);
    if (!this.__callbacks[event]) {
      this.__callbacks[event] = [];
    }
    this.__callbacks[event].push(callback.bind(scope));
  }

  __onModalOpen() {
    const element = document.body, className = 'privacy-consent--active';

    if ('classList' in document.documentElement) {
      element.classList.add(className);
    } else {
      element.className += (element.className ? ' ' : '') + className;
    }
  }

  __onModalClose() {
    const element = document.body, className = 'privacy-consent--active';

    if ('classList' in document.documentElement) {
      element.classList.remove(className);
    } else {
      element.className = element.className.replace(new RegExp('(^|\\s)*' + className + '(\\s|$)*', 'g'), '');
    }
  }

  runCookieStore() {
    const consent = this.getConsent();
    this.__cookieStore
        .map(this.__updateConsentRelevantData.bind(this))
        .forEach(({consentCategory, name, value, props, notOnWhitelist}) => {
          if (!notOnWhitelist && !!consent && !!consent[consentCategory]) {
            this.__log("set cookie", {
              name, value, ...props,
            });
            Cookie.set(name, value, props);
          } else {
            this.__log("remove cookie", {
              name, value, ...props,
            });
            Cookie.remove(name, props);
          }
        });
  }

  __log(...args) {
    if (!!this.options.logging && !!console.debug) {
      console.debug("[PrivacyConsent]", ...args);
    }
  }

  __updateConsentRelevantData(cookieStoreData) {
    if (this.__isWhitelistEnabled()) {
      return cookieStoreData;
    }

    const whitelistEntry = this.__findCookieInWhiteListByName(cookieStoreData);

    if (!!whitelistEntry && whitelistEntry.consentCategory === cookieStoreData.consentCategory) {
      return cookieStoreData;
    }
    this.__log(`no whitelistEntry found for`, cookieStoreData);
    return {
      ...cookieStoreData,
      notOnWhitelist: true,
    };
  };

  __isWhitelistEnabled() {
    return !!!this.__cookieWhitelist.length || !(this.options.whitelist === true);
  }

  __findCookieInWhiteListByCookieStoreData(cookieStoreData) {
    return this.__findCookieInWhiteListByNameDomainPath(cookieStoreData) ||
        this.__findCookieInWhiteListByNamePath(cookieStoreData) ||
        this.__findCookieInWhiteListByNameDomain(cookieStoreData) ||
        this.__findCookieInWhiteListByName(cookieStoreData);
  }

  __findCookieInWhiteListByNameDomainPath(cookieStoreData) {
    return this.__cookieWhitelist
        .find(
            ({name, props}) => name === cookieStoreData.name &&
                props.path === cookieStoreData.props.path &&
                props.domain === cookieStoreData.props.domain
        );
  }

  __findCookieInWhiteListByNamePath(cookieStoreData) {
    return this.__cookieWhitelist
        .find(
            ({name, props}) => name === cookieStoreData.name &&
                props.path === cookieStoreData.props.path
        );
  }

  __findCookieInWhiteListByNameDomain(cookieStoreData) {
    return this.__cookieWhitelist
        .find(
            ({name, props}) => name === cookieStoreData.name &&
                props.domain === cookieStoreData.props.domain
        );
  }

  __findCookieInWhiteListByName(cookieStoreData) {
    return this.__cookieWhitelist.find(({name}) => name === cookieStoreData.name);
  }

  getUrlParams(search) {
    search = search || window.location.search;

    return search.slice(search.indexOf('?') + 1).split('&').reduce((params, hash) => {
      let [key, val] = hash.split('=');

      return {...params, [key]: decodeURIComponent(val)};
    }, {});
  }

  initialize(force) {
    let consent = this.getConsent();
    let invalidConsentState = !this.hasValidPrivacyConsent() || this.__hasMissingConsent(consent);

    const transferredPrivacyConsent = this.getUrlParams()['privacyConsent'];

    if (!!transferredPrivacyConsent && invalidConsentState) {
      try {
        const decodedData = base64.atob(transferredPrivacyConsent);

        this.__updateConsent(this.__validConsent(JSON.parse(decodedData)));
      } catch (e) {
      }

      consent = this.getConsent();
      invalidConsentState = !this.hasValidPrivacyConsent() || this.__hasMissingConsent(consent);
    }


    if (!invalidConsentState) {
      this.__cleanGivenCookies();
    }

    if (invalidConsentState || force) {
      if (!!this.app) {
        this.app.$destroy();
      }

      let config = this.__prepareConfig();
      if (force) {
        config.advancedPageFlow = false;
      }

      this.app = new App({
        target: document.body,
        props: {
          config: config,
          ref: this,
          show: true,
          consent,
        },
      });
      this.__call('onModalOpen');
    }

    return this;
  }

  __cleanGivenCookies() {
    const givenCookieNames = Object.keys(Cookie.get());

    givenCookieNames.map(name => {
      const cookieStoreData = this.__findCookieInWhiteListByName({name});
      if (!(!!cookieStoreData && this.hasConsented(cookieStoreData.consentCategory))) {
        Cookie.remove(name);
        this.__log("removed cookie", name);
      } else {
        this.__log("keep cookie", name)
      }
    })
  }

  getDefaultConsent() {
    return this.options.defaultConsent;
  }

  hasDefaultConsent() {
    return !!this.getDefaultConsent();
  }

  getCookieValue() {
    return Cookie.get(CONSENT_COOKIE);
  }

  hasPrivacyConsentCookie() {
    return !!this.getCookieValue();
  }

  hasValidPrivacyConsent() {
    return this.hasPrivacyConsentCookie() || this.hasDefaultConsent();
  }

  getBase64Consent() {
    return this.getCookieValue() || base64.btoa(JSON.stringify(this.__validConsent()));
  }

  getConsent() {
    try {
      const encodedData = this.getBase64Consent();
      const decodedData = base64.atob(encodedData);

      return this.__validConsent(JSON.parse(decodedData));
    } catch (error) {
      this.__log(`failed parsing ${CONSENT_COOKIE}`, error);
      return this.__validConsent();
    }
  }

  hasConsented(consentCategory, consent) {
    consent = consent || this.getConsent();
    let copy = consentCategory;
    if (!consentCategory) {
      return false;
    } else if (!this.hasValidPrivacyConsent()) {
      return consentCategory === PrivacyConsentEnum.TECHNICAL;
    }

    const negate = consentCategory[0] === "!";
    if (negate) {
      copy = consentCategory.substr(1);
    }

    const result = !!(this.getConsent() || {})[copy];

    return negate ? !result : result;
  }

  updateCookieInWhitelist(whitelistData) {
    if (whitelistData instanceof Array) {
      whitelistData.forEach(this.updateCookieInWhitelist.bind(this));
      return;
    }
    if (!whitelistData.props) {
      whitelistData.props = {};
    }
    const targetData = this.__findCookieInWhiteListByNameDomainPath(whitelistData);

    if (!!targetData) {
      targetData.consentCategory = whitelistData.consentCategory;
      targetData.props = whitelistData.props;
      this.__log("updated in whitelist", targetData);
    } else {
      this.__cookieWhitelist.push(whitelistData);
      this.__log("added to whitelist", whitelistData);
    }
    this.runCookieStore();
  }

  addCookie(name, value, consentCategory, domain, path, expires, secure, sameSite) {
    this.__log("added cookie to cookieStore", {name, value, domain, path});
    this.__cookieStore.push({
      name,
      value,
      consentCategory,
      props: {
        domain,
        path,
        expires,
        secure,
        sameSite
      },
    });
    this.runCookieStore();
  }

  invoke(callback, invokeTrigger, scope, once, ...args) {
    if (!!scope) {
      callback = callback.bind(scope);
    }

    const shouldInvoke = this.__shouldInvokeByTrigger(invokeTrigger);

    if (shouldInvoke) {
      this.__log("invoked", invokeTrigger, once);
      callback(...args);
    }

    if (this.__shouldAddToStore(once, shouldInvoke)) {
      this.__log("added to invokeStore", callback, once);
      this.__invokeStore.push({
        callback,
        invokeTrigger,
        once,
        args,
      });

      return false;
    }

    return true;
  }

  __validConsent(consent) {
    return {...this.__getDefaultConsent(consent), [PrivacyConsentEnum.TECHNICAL]: true};
  }

  __getDefaultConsent(consent) {
    return consent || this.getDefaultConsent() || Object.keys(PrivacyConsentEnum).filter(key => key !== 'NOT').reduce((consent, key) => {
      return {...consent, [PrivacyConsentEnum[key]]: PrivacyConsentEnum[key] === PrivacyConsentEnum.TECHNICAL}
    }, {});
  }

  __hasMissingConsent(consent) {
    const enumKeys = Object.keys(PrivacyConsentEnum)
        .filter(key => key !== "NOT")
        .map(key => PrivacyConsentEnum[key]);
    const consentKeys = Object.keys(consent);
    const missingKey = enumKeys.find(
        enumKey => !consentKeys.find(consentKey => consentKey === enumKey)
    );

    return !!missingKey || enumKeys.length != consentKeys.length;
  }

  __updateConsent(consent) {
    const value = base64.btoa(JSON.stringify(this.__validConsent(consent)));
    Cookie.set(CONSENT_COOKIE, value, {
      path: "/",
      domain: this.__getDomain(),
      expires: COOKIE_EXPIRES,
      secure:  this.hostHasSSL,
      sameSite: this.hostHasSSL ? 'None' : undefined
    });
  }

  __removeConsent() {
    Cookie.remove(CONSENT_COOKIE, {
      path: "/",
      domain: this.__getDomain()
    });
  }

  __call(key, ...data) {
    if (!!this.__callbacks[key]) {
      this.__callbacks[key].forEach(callback => {
        callback(...data);
      });
    }
  }

  __getDomain() {
    const split = window.location.hostname.match(/[^.\s\/]+\.([a-z]{2,}|[a-z]{2}.[a-z]{2})$/);
    if (split != null) {
      return `.${split[0]}`;
    } else {
      return window.location.hostname;
    }
  }

  __onStatusChanged(consent, buttonId) {
    this.__updateConsent(consent);
    this.runCookieStore();
    if (!!this.app) {
      this.app.$destroy();
      this.app = null;
      this.__call('onModalClose', consent, buttonId);
    }
    this.__cleanGivenCookies();
    this.runInvokes();
    this.__handleTIFOptIn(consent);
  }

  __handleTIFOptIn(consent) {
    if (consent.statistics !== true) {
      return;
    }
    let option = 'optout';
    if (consent.marketing === true || consent.partnerships === true) {
      option = 'optin';
    }
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `https://t.ionos.${this.__getTld()}/${option}_p/`);
    xhr.withCredentials = true;
    xhr.send();
  }

  runInvokes(options = {}) {
    let i = 0;
    const startIndex = this.__invokeStore.length;

    while (i < startIndex) {
      const invoker = this.__invokeStore[i];
      const {callback, scope, once, invokeTrigger, args} = invoker;

      if (!!options.onlyOnce && !once) {
        i++;
        continue;
      }

      const shouldInvoke = this.__shouldInvokeByTrigger(invokeTrigger);

      if (shouldInvoke) {
        this.__log("invoked from invokeStore", invokeTrigger, once);
        callback.apply(scope, ...args);
      }
      if (!this.__shouldAddToStore(once, shouldInvoke)) {
        this.__log("removed from invokeStore", invokeTrigger, once);
        this.__invokeStore[i] = null;
      }
      i++;
    }

    this.__invokeStore = this.__invokeStore.filter(e => !!e);
  }

  __shouldInvokeByTrigger(invokeTrigger) {
    if (this.__isFunction(invokeTrigger)) {
      return invokeTrigger();
    }
    return this.hasConsented(invokeTrigger);
  }

  __isFunction(invokerType) {
    return typeof invokerType === "function";
  }

  __shouldAddToStore(once, hasConsented) {
    return (!!once && !hasConsented) || !once;
  }

  __prepareConfig() {
    let lang;
    let langConf;
    let siteConf = siteConfig[this.__getTld()];

    if (this.options.language && Object.keys(langConfig).indexOf(this.options.language) > -1) {
      lang = this.options.language;
    } else {
      lang = siteConf.defaultLang;
    }
    langConf = langConfig[lang];

    langConf.siteConfig = siteConf;

    if (!langConfig[lang].selectAllDefault) {
      langConfig[lang].selectAllDefault = langConfig[lang].selectAll;
    }

    return this.__langConfWithSiteUrls(langConfig[lang], siteConf);
  }

  __langConfWithSiteUrls(langConfig, siteConfig) {
    // replace the placeholders with urls from siteConfig
    langConfig.mainDescription[0] = langConfig.mainDescription[0]
        .replace(/\[LINK_COOKIE_PAGE\]/g, siteConfig.cookiePageUrl)
        .replace(/\[LINK_PRIVACY_TERMS\]/g, siteConfig.privacyTermsUrl);

    return langConfig;
  }

  __getTld() {
    if (typeof UNOUNO !== 'undefined' && UNOUNO.params && UNOUNO.params.siteId) {
      const country = UNOUNO.params.siteId.split('.').pop().toLowerCase();
      return country === 'us' ? 'com' : country;
    }
    const tld = window.location.hostname.split('.').pop();
    return Object.keys(siteConfig).indexOf(tld) > -1 ? tld : 'com';
  }

  __track(consent, buttonId) {
     //only track live
    if (!window.location.hostname.split('.').includes('ionos')) {
      return;
    }

    let visitId = '';
    let actionCode = '';

    if (typeof UNOUNO !== 'undefined') {
      if (UNOUNO.params && UNOUNO.params.ucuoId) {
        visitId = UNOUNO.params.ucuoId;
      }
      if (UNOUNO.tracking && UNOUNO.tracking.hkanalActioncode) {
        actionCode = UNOUNO.tracking.hkanalActioncode;
      }
    }

    const params = {
      technical: consent.technical,
      marketing: consent.marketing,
      partnerships: consent.partnerships,
      statistics: consent.statistics,
      userChoice: buttonId,
      visitId: visitId,
      actionCode: actionCode,
      referrer: window.location.href.split('?')[0],
    };
    const queryString = Object.keys(params).map(key => key + '=' + params[key]).join('&');

    const pixelServerTldMapping = {
      at: 'at',
      ca: 'ca',
      de: 'de',
      es: 'es',
      fr: 'fr',
      it: 'it',
      mx: 'mx',
      uk: 'co.uk',
      us: 'com',
      cloud: 'de'
    };
    const pixelServerTld = pixelServerTldMapping[this.__getTld()] || 'com';
    const url = `https://pixel.ionos.${pixelServerTld}/privacy-consent?${queryString}`;

    const pixel = new Image();
    pixel.src = url
  }
}